import React, { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import type { ChartsAxisContentProps } from '@mui/x-charts';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { translate as t } from 'hooks/useTranslations';

dayjs.extend(customParseFormat);
import { useRiskPoolOrgContext } from '../../RiskPoolOrgContext';
import { usePolicyContext } from '../PolicyContext';
import usePolicyTrends, { getPeriodMode } from '../_data_sources/usePolicyTrends';
import type { PeriodMode } from '../_data_sources/usePolicyTrends';
import useDTBTrends from '../_data_sources/useDTBTrends';
import type { PolicyBarChartDataPoint, DTBBarChartDataPoint } from '../_types';
import { neutral, warning, primary, success, error } from '_lex/_foundations/phxColors';
import OrganizationalTrendsFilters from './OrganizationalTrendsFilters';

type ViewMode = 'policy' | 'ack' | 'dtb';

interface TrendsAxisTooltipContentExtraProps {
  viewMode: ViewMode;
  policyChartData: PolicyBarChartDataPoint[];
  dtbChartData: DTBBarChartDataPoint[];
  formatPeriod: (period: string) => string;
}

/** Axis tooltip: one tooltip per period (whole band hover) with total and full breakout, percent of total. */
function TrendsAxisTooltipContent({
  dataIndex,
  axisValue,
  viewMode,
  policyChartData,
  dtbChartData,
  formatPeriod,
}: ChartsAxisContentProps & TrendsAxisTooltipContentExtraProps) {
  if (typeof dataIndex !== 'number' || dataIndex < 0) return null;

  const chartData = viewMode === 'dtb' ? dtbChartData : policyChartData;
  if (dataIndex >= chartData.length) return null;

  const periodLabel =
    axisValue != null && typeof axisValue === 'string' ? formatPeriod(axisValue) : String(axisValue ?? '');

  const pct = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
  let total: number;
  let lines: Array<{ label: string; count: number; pct: number }> = [];

  if (viewMode === 'policy') {
    const point = policyChartData[dataIndex];
    const pending = point.lexipolUpdates.pendingCount;
    const processed = point.lexipolUpdates.processedCount;
    total = pending + processed;
    lines = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.pendingUpdates'), count: pending, pct: pct(pending, total) },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.processedUpdates'), count: processed, pct: pct(processed, total) },
    ];
  } else if (viewMode === 'ack') {
    const point = policyChartData[dataIndex];
    const ac = point.assignmentsCompleted;
    const anc = point.assignmentsNotCompleted;
    total =
      ac.onTime +
      ac.overdueByLessThanWeek +
      ac.overdueByWeekOrMore +
      anc.overdueByLessThanWeek +
      anc.overdueByWeekOrMore;
    lines = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.completedOnTime'), count: ac.onTime, pct: pct(ac.onTime, total) },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedOverdueLessThanWeek'),
        count: ac.overdueByLessThanWeek,
        pct: pct(ac.overdueByLessThanWeek, total),
      },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedOverdueWeekOrMore'),
        count: ac.overdueByWeekOrMore,
        pct: pct(ac.overdueByWeekOrMore, total),
      },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.notCompletedOverdueLessThanWeek'),
        count: anc.overdueByLessThanWeek,
        pct: pct(anc.overdueByLessThanWeek, total),
      },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.notCompletedOverdueWeekOrMore'),
        count: anc.overdueByWeekOrMore,
        pct: pct(anc.overdueByWeekOrMore, total),
      },
    ];
  } else {
    const point = dtbChartData[dataIndex];
    const ac = point.assignmentsCompleted;
    const anc = point.assignmentsNotCompleted;
    total =
      ac.onTime +
      ac.overdueByLessThanWeek +
      ac.overdueByWeekOrMore +
      anc.overdueByLessThanWeek +
      anc.overdueByWeekOrMore;
    lines = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.completedOnTime'), count: ac.onTime, pct: pct(ac.onTime, total) },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedOverdueLessThanWeek'),
        count: ac.overdueByLessThanWeek,
        pct: pct(ac.overdueByLessThanWeek, total),
      },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedOverdueWeekOrMore'),
        count: ac.overdueByWeekOrMore,
        pct: pct(ac.overdueByWeekOrMore, total),
      },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.notCompletedOverdueLessThanWeek'),
        count: anc.overdueByLessThanWeek,
        pct: pct(anc.overdueByLessThanWeek, total),
      },
      {
        label: t('dashboard.riskPool.riskPoolOrg.trends.notCompletedOverdueWeekOrMore'),
        count: anc.overdueByWeekOrMore,
        pct: pct(anc.overdueByWeekOrMore, total),
      },
    ];
  }

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        minWidth: 220,
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="caption" sx={{ color: 'text.primary' }}>
          {periodLabel}
        </Typography>
        <Typography variant="caption" component="span" sx={{ fontWeight: 700, fontSize: '1.1em', display: 'block' }}>
          {t('dashboard.riskPool.riskPoolOrg.trends.total')}: {total}
        </Typography>
        {lines.map((line) => (
          <Typography key={line.label} variant="caption" sx={{ pl: 1, color: 'text.secondary' }}>
            {line.label}: {line.count} ({line.pct}%)
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

interface OrganizationalTrendsProps {
  selectedManual: string;
  onManualChange: (event: SelectChangeEvent<string>) => void;
  onReportSelect: (reportType: string) => void;
}

export function OrganizationalTrends({ selectedManual, onManualChange, onReportSelect }: OrganizationalTrendsProps) {
  const { orgId, queryParams } = useRiskPoolOrgContext();
  const { manualId } = usePolicyContext();
  const [viewMode, setViewMode] = useState<ViewMode>('policy');

  const { data: policyData, isLoading: isPolicyLoading } = usePolicyTrends(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });
  const { data: dtbData, isLoading: isDTBLoading } = useDTBTrends(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });

  const isLoading = viewMode === 'dtb' ? isDTBLoading : isPolicyLoading;
  const policyChartData = policyData?.metrics ?? [];
  const dtbChartData = dtbData?.metrics ?? [];
  const chartData = viewMode === 'dtb' ? dtbChartData : policyChartData;
  const periodMode: PeriodMode = getPeriodMode(queryParams.startDate ?? '', queryParams.endDate ?? '');

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: ViewMode | null) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <OrganizationalTrendsFilters
          selectedManual={selectedManual}
          onManualChange={onManualChange}
          onReportSelect={onReportSelect}
        />
        <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, mt: 3 }}>
          <CardHeader title={t('dashboard.riskPool.riskPoolOrg.trends.title')} />
          <CardContent>
            <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!chartData.length) {
    return null;
  }

  // Use unique period values for x-axis so each bar has its own band (fixes hover/tooltip when API returns daily data).
  const xAxisData = chartData.map((point) => point.period);
  // Date-only strings (YYYY-MM-DD) parsed as local time. Weeks = start-of-week as MM/DD/YYYY, months = MMM YYYY.
  const formatAxisPeriod = (period: string) =>
    periodMode === 'weeks'
      ? dayjs(period, 'YYYY-MM-DD', true).format('MM/DD/YYYY')
      : dayjs(period, 'YYYY-MM-DD', true).format('MMM YYYY');

  // Prepare series and legend based on view mode
  let series: any[] = [];
  let legendItems: Array<{ label: string; color: string }> = [];

  if (viewMode === 'policy' && policyChartData.length > 0) {
    series = [
      {
        id: 'pending',
        label: t('dashboard.riskPool.riskPoolOrg.trends.pendingUpdates'),
        data: policyChartData.map((d) => d.lexipolUpdates.pendingCount),
        color: warning[600],
      },
      {
        id: 'processed',
        label: t('dashboard.riskPool.riskPoolOrg.trends.processedUpdates'),
        data: policyChartData.map((d) => d.lexipolUpdates.processedCount),
        color: warning[200],
      },
    ];

    legendItems = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.pendingUpdates'), color: warning[600] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.processedUpdates'), color: warning[200] },
    ];
  } else if (viewMode === 'ack' && policyChartData.length > 0) {
    series = [
      {
        id: 'completed',
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'),
        data: policyChartData.map(
          (d) =>
            d.assignmentsCompleted.onTime +
            d.assignmentsCompleted.overdueByLessThanWeek +
            d.assignmentsCompleted.overdueByWeekOrMore
        ),
        color: primary[200],
      },
      {
        id: 'overdue',
        label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'),
        data: policyChartData.map(
          (d) => d.assignmentsNotCompleted.overdueByLessThanWeek + d.assignmentsNotCompleted.overdueByWeekOrMore
        ),
        color: primary[500],
      },
    ];

    legendItems = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'), color: primary[200] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'), color: primary[500] },
    ];
  } else if (viewMode === 'dtb' && dtbChartData.length > 0) {
    series = [
      {
        id: 'assignmentsCompleted',
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'),
        data: dtbChartData.map(
          (d) =>
            d.assignmentsCompleted.onTime +
            d.assignmentsCompleted.overdueByLessThanWeek +
            d.assignmentsCompleted.overdueByWeekOrMore
        ),
        color: success[600],
      },
      {
        id: 'assignmentsNotCompleted',
        label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'),
        data: dtbChartData.map(
          (d) => d.assignmentsNotCompleted.overdueByLessThanWeek + d.assignmentsNotCompleted.overdueByWeekOrMore
        ),
        color: error[500],
      },
    ];

    legendItems = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'), color: success[600] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'), color: error[500] },
    ];
  }

  return (
    <Box>
      <OrganizationalTrendsFilters
        selectedManual={selectedManual}
        onManualChange={onManualChange}
        onReportSelect={onReportSelect}
      />
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, mt: 3 }}>
        <CardHeader
          title={t('dashboard.riskPool.riskPoolOrg.trends.title')}
          action={
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              sx={{
                borderRadius: 2,
                border: `1px solid ${neutral[200]}`,
                overflow: 'hidden',
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 1.5,
                  border: 'none',
                  borderRight: `1px solid ${neutral[200]}`,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  color: neutral[800],
                  textTransform: 'none',
                  '&:last-of-type': {
                    borderRight: 'none',
                  },
                  '&.Mui-selected': {
                    backgroundColor: neutral[50],
                    color: neutral[800],
                  },
                  '&:not(.Mui-selected)': {
                    backgroundColor: neutral[25],
                  },
                },
              }}
            >
              <ToggleButton value='policy'>{t('dashboard.riskPool.riskPoolOrg.trends.policyTab')}</ToggleButton>
              <ToggleButton value='ack'>{t('dashboard.riskPool.riskPoolOrg.trends.ackTab')}</ToggleButton>
              <ToggleButton value='dtb'>{t('dashboard.riskPool.riskPoolOrg.trends.dtbTab')}</ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          {/* Chart */}
          <Box sx={{ width: '100%', height: 287, pt: 2.5, pb: 2.5, backgroundColor: 'white' }}>
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  data: xAxisData,
                  valueFormatter: (value: string) => formatAxisPeriod(value),
                  tickNumber: Math.min(12, xAxisData.length),
                },
              ]}
              series={series}
              height={287}
              margin={{ top: 10, bottom: 30, left: 60, right: 10 }}
              slots={{
                axisContent: (props: ChartsAxisContentProps) => (
                  <TrendsAxisTooltipContent
                    {...props}
                    viewMode={viewMode}
                    policyChartData={policyChartData}
                    dtbChartData={dtbChartData}
                    formatPeriod={formatAxisPeriod}
                  />
                ),
              }}
              slotProps={{
                legend: { hidden: true },
              }}
              tooltip={{ trigger: 'axis' }}
            />
          </Box>

          {/* Legend */}
          <Box
            sx={{
              pt: 6,
              pb: 2,
            }}
          >
            <Stack
              direction='row'
              spacing={4}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {legendItems.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      color: neutral[500],
                      fontSize: 12,
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      lineHeight: '16.8px',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default OrganizationalTrends;
